import { Box } from "@mui/material";
import { AddOutlined } from "@mui/icons-material";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { normalizeIsoStringToUtc } from "../../../../../_src_shared/utils/functions/formatDateTime";
import { formatDate } from "../../../../../_src_shared/utils/functions/formatDate";

/* ───────────────── palette + shadow ─────────────────
   Editorial / financial-document feel — deep forest accent for milestones,
   burnt amber for things needing attention, off-white surfaces with
   thin hairlines instead of MUI's default greys. */
export const PALETTE = {
  bg: "#fafaf7",
  surface: "#ffffff",
  surface2: "#f5f4ef",
  ink: "#15151a",
  ink2: "#4a4a55",
  ink3: "#8a8a95",
  ink4: "#c4c4cc",
  line: "#e8e6df",
  line2: "#efede6",
  accent: "#1a3d2e",
  accentSoft: "#e8f0eb",
  warn: "#b8641d",
  warnSoft: "#fbf0e2",
  good: "#2d6a4a",
  goodSoft: "#e3efe7",
};

export const SHADOW_SM =
  "0 1px 0 rgba(20,20,30,0.04), 0 1px 2px rgba(20,20,30,0.04)";

export const FONT_SERIF = '"Fraunces", "Source Serif Pro", Georgia, serif';
export const FONT_MONO = '"JetBrains Mono", "SF Mono", Menlo, monospace';

/* ───────────────── helpers ───────────────── */

// Backend ISO strings often lack a timezone marker (e.g. "2026-04-29T22:18:14"
// with no trailing 'Z' or ±hh:mm offset) but represent UTC. Native Date()
// parses unmarked strings as the *user's local time*, which throws off any
// "minutes/hours/days from now" math by the local UTC offset. Route every
// timestamp through normalizeIsoStringToUtc first so this tab stays
// consistent with formatDateTime's display logic — same source-of-truth for
// when an event happened, whether we're showing it ("5/4/2026 12:44 PM") or
// computing relative distance ("2h ago").
export const parseIsoMs = (v) => {
  if (!v) return null;
  const normalized = typeof v === "string" ? normalizeIsoStringToUtc(v) : v;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
};

export const parseIso = (v) => {
  const ms = parseIsoMs(v);
  return ms == null ? null : new Date(ms);
};

export const fmtDate = (v) => {
  const d = parseIso(v);
  if (!d) return null;
  return formatDate(v, "", "MMM d, yyyy");
};

export const fmtDateShort = (v) => {
  const d = parseIso(v);
  if (!d) return null;
  return formatDate(v, "", "MMM d");
};

export const daysFromNow = (v) => {
  const ms = parseIsoMs(v);
  if (ms == null) return null;
  return Math.round((ms - Date.now()) / 86_400_000);
};

// Tight duration label for stage segments: "11d", "2mo", "5h", "1y".
// Same unit-scaling as fmtRelativeShort but unsigned (durations have no
// past/future direction).
export const fmtDuration = (ms) => {
  if (ms == null || Number.isNaN(ms) || ms < 0) return null;
  if (ms < 60_000) return "<1m";
  let val;
  let unit;
  if (ms < 3_600_000) {
    val = Math.round(ms / 60_000);
    unit = "m";
  } else if (ms < 86_400_000) {
    val = Math.round(ms / 3_600_000);
    unit = "h";
  } else if (ms < 86_400_000 * 30) {
    val = Math.round(ms / 86_400_000);
    unit = "d";
  } else if (ms < 86_400_000 * 365) {
    val = Math.round(ms / (86_400_000 * 30));
    unit = "mo";
  } else {
    val = Math.round(ms / (86_400_000 * 365));
    unit = "y";
  }
  return `${val}${unit}`;
};

// Tight relative-time format for date cards: "just now", "1h ago", "5d ago",
// "11mo ago", "in 5d". Auto-scales unit so the badge stays small.
export const fmtRelativeShort = (v) => {
  const t = parseIsoMs(v);
  if (t == null) return null;
  const diffMs = t - Date.now();
  const past = diffMs < 0;
  const abs = Math.abs(diffMs);
  if (abs < 60_000) return "just now";
  let val;
  let unit;
  if (abs < 3_600_000) {
    val = Math.round(abs / 60_000);
    unit = "m";
  } else if (abs < 86_400_000) {
    val = Math.round(abs / 3_600_000);
    unit = "h";
  } else if (abs < 86_400_000 * 30) {
    val = Math.round(abs / 86_400_000);
    unit = "d";
  } else if (abs < 86_400_000 * 365) {
    val = Math.round(abs / (86_400_000 * 30));
    unit = "mo";
  } else {
    val = Math.round(abs / (86_400_000 * 365));
    unit = "y";
  }
  return past ? `${val}${unit} ago` : `in ${val}${unit}`;
};

// Earliest-uploaded loanDocument matching one of the candidate docTypes.
export const firstDocByType = (docs, candidates) => {
  if (!Array.isArray(docs) || docs.length === 0) return null;
  const lc = candidates.map((c) => c.toLowerCase());
  const matches = docs.filter((d) =>
    lc.includes((d?.docType || "").toLowerCase()),
  );
  if (matches.length === 0) return null;
  matches.sort(
    (a, b) =>
      (parseIsoMs(a?.uploadDate) ?? 0) - (parseIsoMs(b?.uploadDate) ?? 0),
  );
  return matches[0];
};

/* ───────────────── primitives ───────────────── */

// Panel — the "card" chrome used by every section. White surface, hairline
// border, soft shadow, fixed radius. Built locally rather than reusing
// CardPaper so the design tokens (line color, radius, shadow weight) stay
// internally consistent across this tab.
export const Panel = ({ title, count, children, sx }) => (
  <Box
    sx={{
      background: PALETTE.surface,
      border: `1px solid ${PALETTE.line}`,
      borderRadius: "10px",
      boxShadow: SHADOW_SM,
      overflow: "hidden",
      ...sx,
    }}
  >
    {(title || count != null) && (
      <Flx
        ac
        jb
        sx={{
          px: "22px",
          pt: "18px",
          pb: "14px",
          borderBottom: `1px solid ${PALETTE.line2}`,
        }}
      >
        <Txt
          component="h3"
          sx={{
            fontWeight: 700,
            fontSize: 16,
            letterSpacing: "-0.01em",
            m: 0,
            color: PALETTE.ink,
          }}
        >
          {title}
        </Txt>
        {count != null && (
          <Txt
            component="div"
            sx={{
              fontSize: 11,
              fontFamily: FONT_MONO,
              color: PALETTE.ink3,
              background: PALETTE.surface2,
              px: "7px",
              py: "2px",
              borderRadius: "4px",
              border: `1px solid ${PALETTE.line2}`,
              fontWeight: 400,
            }}
          >
            {count}
          </Txt>
        )}
      </Flx>
    )}
    <Box sx={{ py: "6px" }}>{children}</Box>
  </Box>
);

// Timeline marker — absolutely positioned along the track. `state` controls
// the dot styling: today (highlighted), done (filled), next (ring),
// urgent (amber), pending (open).
export const TimelineMarker = ({
  leftPct,
  label,
  dateText,
  state = "pending",
}) => {
  const isToday = state === "today";
  const dotStyles = (() => {
    switch (state) {
      case "today":
        return {
          background: PALETTE.accent,
          borderColor: PALETTE.accent,
          // Larger glow halo + slightly bigger dot makes the "you are here"
          // marker pop against the others.
          width: 13,
          height: 13,
          boxShadow: `0 0 0 6px ${PALETTE.accentSoft}, 0 0 0 7px ${PALETTE.accent}1f`,
        };
      case "done":
        return {
          background: PALETTE.accent,
          borderColor: PALETTE.accent,
        };
      case "next":
        return {
          background: PALETTE.surface,
          borderColor: PALETTE.accent,
          boxShadow: `0 0 0 4px ${PALETTE.accentSoft}`,
        };
      case "urgent":
        return {
          background: PALETTE.warn,
          borderColor: PALETTE.warn,
          boxShadow: `0 0 0 4px ${PALETTE.warnSoft}`,
        };
      default:
        return {
          background: PALETTE.surface,
          borderColor: PALETTE.ink4,
        };
    }
  })();
  const labelColor = isToday ? PALETTE.accent : PALETTE.ink3;
  const dateColor = isToday
    ? PALETTE.accent
    : state === "next"
      ? PALETTE.accent
      : state === "urgent"
        ? PALETTE.warn
        : PALETTE.ink2;
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: `${leftPct}%`,
        transform: "translateX(-50%)",
        textAlign: "center",
        // Auto-sized to fit the label, capped so a long string can wrap to a
        // second line instead of overflowing into a neighbour marker.
        width: "max-content",
        maxWidth: 110,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Fixed-height label slot keeps the dot aligned with the track line
          (at y=28px) regardless of whether the label is one or two lines. */}
      <Box
        sx={{
          height: 22,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
        }}
      >
        <Txt
          component="div"
          sx={{
            fontSize: 10.5,
            color: labelColor,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: isToday ? 700 : 500,
            lineHeight: 1.15,
            textAlign: "center",
          }}
        >
          {label}
        </Txt>
      </Box>
      <Box
        sx={{
          width: 11,
          height: 11,
          borderRadius: "50%",
          // Breathing room between label slot (22px tall) and the dot —
          // pairs with the track line's top offset below. Today is rendered
          // 2px larger, so nudge mt up by 1px to keep the dot center on the
          // same Y as the other markers.
          mt: isToday ? "9px" : "10px",
          position: "relative",
          zIndex: 2,
          border: "2px solid",
          ...dotStyles,
        }}
      />
      <Txt
        component="div"
        sx={{
          fontSize: isToday ? 12.5 : 12,
          color: dateColor,
          fontFamily: FONT_MONO,
          mt: "6px",
          fontWeight:
            isToday || state === "next" || state === "urgent" ? 600 : 400,
          whiteSpace: "nowrap",
        }}
      >
        {dateText || "—"}
      </Txt>
    </Box>
  );
};

// DateRow — one row in the "Upcoming dates" panel.
export const DateRow = ({
  when,
  whenSub,
  what,
  whatSub,
  badgeLabel,
  badgeTone = "pending",
  state = "default",
  isFirst,
}) => {
  const isUrgent = state === "urgent";
  const isDone = state === "done";
  const badgeColors = (() => {
    switch (badgeTone) {
      case "received":
        return { bg: PALETTE.goodSoft, fg: PALETTE.good };
      case "urgent":
        return { bg: PALETTE.warnSoft, fg: PALETTE.warn };
      default:
        return { bg: PALETTE.surface2, fg: PALETTE.ink3 };
    }
  })();
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 110px",
        alignItems: "center",
        gap: "18px",
        px: "22px",
        py: "14px",
        borderTop: isFirst ? "none" : `1px solid ${PALETTE.line2}`,
        borderLeft: `2px solid ${isUrgent ? PALETTE.warn : "transparent"}`,
        background: isUrgent
          ? `linear-gradient(90deg, ${PALETTE.warnSoft} 0%, transparent 40%)`
          : "transparent",
        opacity: isDone ? 0.62 : 1,
        transition: "background 0.15s",
        "&:hover": {
          background: isUrgent
            ? `linear-gradient(90deg, ${PALETTE.warnSoft} 0%, transparent 40%)`
            : PALETTE.surface2,
        },
      }}
    >
      <Box>
        <Txt component="div" sx={{ fontSize: 13.5, color: PALETTE.ink }}>
          {what}
        </Txt>
        {whatSub && (
          <Txt
            component="div"
            sx={{ color: PALETTE.ink3, fontSize: 12, mt: "1px" }}
          >
            {whatSub}
          </Txt>
        )}
      </Box>
      <Box>
        <Txt
          component="div"
          sx={{
            fontFamily: FONT_MONO,
            fontSize: 13,
            fontWeight: 500,
            color: PALETTE.ink,
            lineHeight: 1.3,
          }}
        >
          {when || "—"}
        </Txt>
        {whenSub && (
          <Txt
            component="div"
            sx={{
              fontSize: 10.5,
              color: isUrgent ? PALETTE.warn : PALETTE.ink3,
              fontWeight: 400,
              mt: "2px",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {whenSub}
          </Txt>
        )}
      </Box>

      {/* <Box
        sx={{
          fontSize: 10.5,
          fontFamily: FONT_MONO,
          fontWeight: 500,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          px: 1,
          py: "3px",
          borderRadius: "4px",
          background: badgeColors.bg,
          color: badgeColors.fg,
        }}
      >
        {badgeLabel}
      </Box> */}
    </Box>
  );
};

// ContactItem — one entry in the "Loan contacts" panel.
export const ContactItem = ({ role, name, org, isFirst, missing }) => (
  <Flx
    sx={{
      px: "22px",
      py: "14px",
      borderTop: isFirst ? "none" : `1px solid ${PALETTE.line2}`,
    }}
    jb
    g={2}
  >
    <Box>
      <Txt
        component="div"
        sx={{
          fontSize: 10.5,
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          color: PALETTE.ink3,
          fontWeight: 500,
          mb: "3px",
        }}
      >
        {role}
      </Txt>
      <Txt
        component="div"
        sx={{
          fontSize: 14,
          color: missing ? PALETTE.ink3 : PALETTE.ink,
          fontWeight: missing ? 400 : 500,
          fontStyle: missing ? "italic" : "normal",
        }}
      >
        {name}
      </Txt>
      {org && (
        <Txt
          component="div"
          sx={{ fontSize: 12.5, color: PALETTE.ink3, mt: "1px" }}
        >
          {org}
        </Txt>
      )}
    </Box>
    {missing && (
      <Box
        component="button"
        sx={{
          alignSelf: "flex-start",
          fontSize: 11,
          color: PALETTE.accent,
          background: "none",
          border: `1px dashed ${PALETTE.ink4}`,
          px: "10px",
          py: "4px",
          borderRadius: "4px",
          cursor: "pointer",
          fontFamily: "inherit",
          transition: "all 0.15s",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          "&:hover": {
            borderColor: PALETTE.accent,
            background: PALETTE.accentSoft,
          },
        }}
      >
        <AddOutlined sx={{ fontSize: 12 }} />
        Add
      </Box>
    )}
  </Flx>
);

// ComplianceItem — single check row in the TRID compliance grid.
export const ComplianceItem = ({ label, status, complete }) => (
  <Flx
    ac
    jb
    g={1.5}
    sx={{
      background: PALETTE.surface,
      px: "18px",
      py: "14px",
    }}
  >
    <Flx ac g={1.25}>
      <Box
        sx={{
          width: 14,
          height: 14,
          borderRadius: "50%",
          border: `1.5px solid ${complete ? PALETTE.good : PALETTE.ink4}`,
          background: complete ? PALETTE.good : "transparent",
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          "&::after": complete
            ? {
                content: '""',
                width: 4,
                height: 7,
                border: "solid white",
                borderWidth: "0 1.5px 1.5px 0",
                transform: "rotate(45deg) translate(-1px, -1px)",
              }
            : undefined,
        }}
      />
      <Txt
        component="div"
        sx={{
          fontSize: 12.5,
          color: complete ? PALETTE.ink : PALETTE.ink2,
        }}
      >
        {label}
      </Txt>
    </Flx>
    <Txt
      component="div"
      sx={{
        fontSize: 10.5,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: complete ? PALETTE.good : PALETTE.ink3,
        fontFamily: FONT_MONO,
      }}
    >
      {status}
    </Txt>
  </Flx>
);

// DocCard — one column of the document checklist grid.
export const DocCard = ({ title, items, allowUpload = true }) => {
  const total = items.length;
  const done = items.filter((i) => i.complete).length;
  const pct = total === 0 ? 0 : (done / total) * 100;
  const isEmpty = done === 0;
  return (
    <Box
      sx={{
        background: PALETTE.surface,
        border: `1px solid ${PALETTE.line}`,
        borderRadius: "10px",
        boxShadow: SHADOW_SM,
        p: "18px 20px",
        display: "flex",
        flexDirection: "column",
        minHeight: 200,
      }}
    >
      <Flx ac jb sx={{ mb: "14px" }}>
        <Txt
          component="div"
          sx={{ fontSize: 13.5, fontWeight: 600, color: PALETTE.ink }}
        >
          {title}
        </Txt>
        <Txt
          component="div"
          sx={{ fontSize: 11, fontFamily: FONT_MONO, color: PALETTE.ink3 }}
        >
          {done} / {total}
        </Txt>
      </Flx>
      <Box
        sx={{
          height: 3,
          background: PALETTE.line2,
          borderRadius: "2px",
          overflow: "hidden",
          mb: "14px",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${pct}%`,
            background: isEmpty ? PALETTE.ink4 : PALETTE.accent,
            transition: "width 0.4s",
          }}
        />
      </Box>
      <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, flex: 1 }}>
        {items.map((item) => (
          <Flx
            ac
            g={1.25}
            component="li"
            key={item.key}
            sx={{
              py: "6px",
              fontSize: 12.5,
              color: item.complete ? PALETTE.ink : PALETTE.ink2,
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "3px",
                border: `1.5px solid ${item.complete ? PALETTE.accent : PALETTE.ink4}`,
                background: item.complete ? PALETTE.accent : "transparent",
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                "&::after": item.complete
                  ? {
                      content: '""',
                      width: 4,
                      height: 7,
                      border: "solid white",
                      borderWidth: "0 1.5px 1.5px 0",
                      transform: "rotate(45deg) translate(-1px, -1px)",
                    }
                  : undefined,
              }}
            />
            <Txt
              component="span"
              sx={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.label}
            </Txt>
            {item.complete && item.fileExt && (
              <Txt
                component="span"
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  color: PALETTE.ink3,
                }}
              >
                ·{item.fileExt}
              </Txt>
            )}
          </Flx>
        ))}
      </Box>
      {isEmpty && allowUpload && (
        <Box
          component="button"
          sx={{
            mt: "12px",
            background: "none",
            border: `1px dashed ${PALETTE.ink4}`,
            color: PALETTE.ink3,
            p: "8px",
            borderRadius: "6px",
            fontSize: 11.5,
            cursor: "pointer",
            fontFamily: "inherit",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 500,
            transition: "all 0.15s",
            "&:hover": {
              borderColor: PALETTE.accent,
              color: PALETTE.accent,
              background: PALETTE.accentSoft,
            },
          }}
        >
          + Upload documents
        </Box>
      )}
    </Box>
  );
};
